import {useEffect} from 'react';

import {LivePreviewServiceInstance} from 'Application/services/LivePreviewService';

import type {WithChildren} from 'types/global';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface ComponentProps {
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'LivePreviewReady'
     */
    testId?: string;
}

/**
 * A React component that signals readiness to the live preview service and renders its children.
 * This component is used to notify the `LivePreviewServiceInstance` that the page is ready for live preview functionality.
 * It triggers the readiness signal exactly once on mount using `useEffect`, passing a predefined preview origin URL.
 * After signaling readiness, it simply renders the `children` passed to it. This component is useful for wrapping
 * previewable content within a Payload CMS or headless CMS preview workflow.
 *
 * @param props          The component props.
 * @param props.children The children elements to render after signaling preview readiness.
 * @returns A React element that renders its children once live preview has been marked as ready.
 *
 * @example
 * ```tsx
 * <LivePreviewReady>
 *   <MainContent />
 * </LivePreviewReady>
 * ```
 */
// eslint-disable-next-line @typescript-eslint/promise-function-async
const LivePreviewReady = ({children}: WithChildren<ComponentProps>) => {
    useEffect(() => {
        let preview = false;

        if (typeof window !== 'undefined') {
            const query = new URLSearchParams(window.location.search);

            preview = query.has('preview');
        }

        if (preview) {
            LivePreviewServiceInstance.ready();
        }
    }, []);

    return children;
};

LivePreviewReady.displayName = 'LivePreviewReady';

export {LivePreviewReady};